"use client";

import { useEffect, useId, useRef, useState } from "react";
import { AnimatePresence, motion } from "motion/react";
import { cn } from "@/lib/utils";

interface TooltipProps {
  /** The visible, interactive thing the tooltip describes — a span,
   *  button, link, pill, etc. Wrapped so hover/focus on it opens the
   *  panel. */
  children: React.ReactNode;
  /** Content inside the panel. Keep it to a sentence or two. */
  label: React.ReactNode;
  /** Panel position relative to the trigger. */
  side?: "top" | "bottom";
  /** Override the panel max-width for legends/longer copy. */
  panelClassName?: string;
  /** Extra classes on the wrapper span. */
  className?: string;
  /** Disable the panel entirely (e.g. when there's nothing to say). */
  disabled?: boolean;
}

/**
 * Generic hover/focus/tap tooltip. Wrap ANY trigger (pill, button,
 * icon, disabled control) and render theme-aware tooltip copy from
 * `label`.
 *
 * Replaces:
 *   • Native `title="…"` — OS-default typography, no theming, no
 *     delay control, flaky on disabled elements.
 *   • `aria-label="…"` when used as a hover hint — aria-label is
 *     for SR semantics only and is invisible to sighted users.
 *
 * Differs from `HelpTip` (which renders its own `?` icon trigger):
 * here the trigger is whatever you pass as children. Share the same
 * panel styling (border + elevation + arrow) so the app has one
 * tooltip look everywhere.
 *
 * Hover to peek, click to pin, Escape / outside-click to dismiss.
 * Wrapper span catches events even when the inner element is
 * `disabled` — disabled buttons swallow their own pointer events,
 * so native tooltips fire only sporadically across browsers.
 */
export function Tooltip({
  children,
  label,
  side = "top",
  panelClassName,
  className,
  disabled,
}: TooltipProps) {
  const [open, setOpen] = useState(false);
  const [pinned, setPinned] = useState(false);
  const wrapRef = useRef<HTMLSpanElement>(null);
  const panelId = useId();

  useEffect(() => {
    if (!open || !pinned) return;
    const onClick = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        setOpen(false);
        setPinned(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setOpen(false);
        setPinned(false);
      }
    };
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open, pinned]);

  if (disabled) {
    return <>{children}</>;
  }

  return (
    <span
      ref={wrapRef}
      className={cn("relative inline-flex", className)}
      aria-describedby={open ? panelId : undefined}
      onMouseEnter={() => {
        if (!pinned) setOpen(true);
      }}
      onMouseLeave={() => {
        if (!pinned) setOpen(false);
      }}
      onFocus={() => setOpen(true)}
      onBlur={() => {
        if (!pinned) setOpen(false);
      }}
      onClick={(e) => {
        // Pin on click of the wrapper itself — lets the tooltip stay
        // open when the user wants to read longer copy. Only trigger
        // when the click isn't on an interactive descendant (buttons,
        // links) that has its own purpose.
        const target = e.target as HTMLElement;
        const interactive = target.closest("button, a, input, select, textarea");
        if (interactive && wrapRef.current?.contains(interactive)) return;
        setPinned((p) => !p);
        setOpen((o) => !o);
      }}
    >
      {children}
      {/*
        AnimatePresence-driven enter/exit. Y-offset is small (4px from
        the trigger) so the tooltip reads as "lifting off" rather than
        sliding in from far away. `motion.span` keeps the inline-flex
        flow correct so the trigger isn't shoved by an absolutely-
        positioned div.
      */}
      <AnimatePresence>
        {open && (
          <motion.span
            id={panelId}
            role="tooltip"
            initial={{ opacity: 0, y: side === "top" ? 4 : -4, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: side === "top" ? 4 : -4, scale: 0.96 }}
            transition={{ duration: 0.14, ease: "easeOut" }}
            className={cn(
              "absolute z-[var(--z-tooltip,50)] left-1/2 -translate-x-1/2 w-max max-w-[18rem]",
              "rounded-[var(--radius-lg)] border border-[var(--color-border-default)]",
              "bg-[var(--color-bg-elevated)] text-[var(--color-text-primary)]",
              "px-[var(--space-3)] py-[var(--space-2)]",
              "text-[length:var(--font-size-sm)] leading-[var(--line-height-relaxed)]",
              "shadow-[var(--shadow-md)]",
              "pointer-events-none",
              side === "top"
                ? "bottom-full mb-[var(--space-2)]"
                : "top-full mt-[var(--space-2)]",
              panelClassName,
            )}
          >
            {label}
            <span
              aria-hidden="true"
              className={cn(
                "absolute left-1/2 -translate-x-1/2 block size-2 rotate-45",
                "border border-[var(--color-border-default)] bg-[var(--color-bg-elevated)]",
                side === "top"
                  ? "-bottom-1 border-t-0 border-l-0"
                  : "-top-1 border-b-0 border-r-0",
              )}
            />
          </motion.span>
        )}
      </AnimatePresence>
    </span>
  );
}
